import React, { useEffect, useState } from 'react';
import { IconTarget, IconRocket } from '~misc/icons';

export default function TargetBadge({
  label, value, displayValue, isBonus, hitNow, active = false,
  coverageCard = 0, coverageCircle = 0,
  colors = { green: '#9ACA3C', greenLight: '#9bca3cc0', silver: '#9DA2B6' },
  setCardRef, setCircleRef
}) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    let id;
    if (active) {
      setExpanded(true);
      id = setInterval(() => setExpanded((v) => !v), 1000);
    } else {
      setExpanded(false);
    }
    return () => id && clearInterval(id);
  }, [active]);

  const activeOrHover = active || hovered;
  const scale = activeOrHover ? (expanded ? 1.08 : 1) : 1;
  const transformStyle = { transform: `scale(${scale})`, transition: 'transform 900ms cubic-bezier(0.4,0,0.2,1)' };
  const formatTons = (v) => {
    if (!Number.isFinite(v)) return '-';
    const abs = Math.abs(v);
    const opts = abs < 100 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { maximumFractionDigits: 0 };
    const rounded = abs < 100 ? v : Math.round(v);
    return rounded.toLocaleString('pt-BR', opts) + 't';
  };
  const formattedValue = (displayValue != null ? displayValue : formatTons(value));
  const longValue = typeof formattedValue === 'string' ? formattedValue.length >= 10 : false;
  const valueSizeClass = longValue ? 'text-[15px] md:text-[20px]' : 'text-xl md:text-2xl';
  const hitColor = isBonus ? '#F5C115' : '#9ACA3C';
  const hitBgClass = hitNow ? (isBonus ? 'bg-[#F5C115]' : 'bg-[#9ACA3C]') : 'bg-[#9DA2B6]';
 
  return (
    <div
      className="absolute -translate-x-1/2 flex flex-col items-center z-[100] pointer-events-auto"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
      className={`z-[121] relative top-2 flex flex-col items-center rounded-[10px] shadow-sm font-bold text-white border border-white
        ${isBonus ? 'h-[44px] md:h-[50px] w-[75px] md:w-[110px]' : 'h-[44px] md:h-[50px] w-[80px] md:w-[124px] p-1'}
          ${hitBgClass}`}
        style={{}}
        ref={setCardRef}
      >
        <div className={`${isBonus ? 'text-[18px] md:text-[20px] leading-[40px] md:leading-[50px]' : 'text-[14px] md:text-[16px] leading-none'} font-semibold`}>
          {label}
        </div>
        {!isBonus && value != null && (
          <div className={`${valueSizeClass} font-semibold -mt-1.5`}>
            {formattedValue}
          </div>
        )}
        <div className="absolute top-full left-1/2 -translate-x-1/2 h-0 w-0" aria-hidden>
          <span
            className="absolute left-1/2 -translate-x-1/2"
            style={{ width: 0, height: 0, borderLeft: '12px solid transparent', borderRight: '12px solid transparent', borderTop: '14px solid #ffffff' }}
          />
          <span
            className="absolute left-1/2 -translate-x-1/2"
            style={{ width: 0, height: 0, borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: `12px solid ${hitNow ? hitColor : '#9DA2B6'}` }}
          />
        </div>
      </div>
      <div className="mt-1">
        <div className="w-[75px] h-[72px] md:w-[85px] md:h-[80px] grid place-items-center">
          <div
            className={`relative w-12 h-12 md:w-14 md:h-14 z-[120] rounded-full grid place-items-center transition-colors duration-200 pointer-events-auto ${activeOrHover ? 'ring-[12px] ring-[#E9E9E9]/50' : 'hover:ring-[12px] hover:ring-[#E9E9E9]/50'}`}
            style={{
              ...(hitNow
                ? { backgroundColor: hitColor }
                : (isBonus
                    ? { backgroundImage: `linear-gradient(to right, #F5C115 ${Math.round(coverageCircle*100)}%, ${colors.silver} ${Math.round(coverageCircle*100)}%)` }
                    : { backgroundImage: `linear-gradient(to right, ${colors.greenLight} ${Math.round(coverageCircle*100)}%, ${colors.silver} ${Math.round(coverageCircle*100)}%)` }
                  )
              ),
              ...transformStyle
            }}
            ref={setCircleRef}
          >
            {activeOrHover && (
              <>
                <div
                  className="absolute rounded-full border-2 opacity-40 animate-ping pointer-events-none md:hidden"
                  style={{ width: '48px', height: '48px', borderColor: hitNow ? hitColor : colors.silver, zIndex: 110, animationDuration: '2.6s' }}
                />
                <div
                  className="absolute rounded-full border-2 opacity-30 animate-ping pointer-events-none md:hidden"
                  style={{ width: '48px', height: '48px', borderColor: hitNow ? hitColor : colors.silver, zIndex: 110, animationDelay: '1.3s', animationDuration: '2.6s' }}
                />
                <div
                  className="absolute hidden md:block rounded-full border-2 opacity-40 animate-ping pointer-events-none"
                  style={{ width: '56px', height: '56px', borderColor: hitNow ? hitColor : colors.silver, zIndex: 110, animationDuration: '2.6s' }}
                />
                <div
                  className="absolute hidden md:block rounded-full border-2 opacity-30 animate-ping pointer-events-none"
                  style={{ width: '56px', height: '56px', borderColor: hitNow ? hitColor : colors.silver, zIndex: 110, animationDelay: '1.3s', animationDuration: '2.6s' }}
                />
              </>
            )}
            {isBonus ? (
              <IconRocket
                className="absolute z-[300] pointer-events-none w-8 h-8 md:w-auto md:h-auto"
                style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}
                color="#FFFFFF"
              />
            ) : (
              <IconTarget
                className="absolute z-[300] pointer-events-none w-8 h-8 md:w-auto md:h-auto"
                style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}